import { isCarRentalItineraryItem } from './rentalRouteUtils';
import { isFlightItineraryItem } from './flightRouteUtils';

export const buildLocationMapsUrl = (location) => {
  const value = (location || '').toString().trim();
  if (!value) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`;
};

export const getItemMapsUrl = (item) => {
  if (!item) return null;
  if (item.google_map_link) return item.google_map_link;
  if (item.google_maps_url) return item.google_maps_url;
  return buildLocationMapsUrl(item.location);
};

const parseDateTimeParts = (datetimeStr) => {
  if (!datetimeStr) return null;
  const dateStr = datetimeStr.toString();
  let datePart;
  let timePart;
  if (dateStr.includes('T')) {
    [datePart, timePart] = dateStr.split('T');
    timePart = timePart.split('.')[0];
  } else {
    [datePart, timePart] = dateStr.split(' ');
  }
  if (!datePart || !timePart) return null;
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = timePart.split(':').map(Number);
  return { year, month, day, hours, minutes, seconds: seconds || 0 };
};

export const formatTime24 = (datetimeStr) => {
  const parts = parseDateTimeParts(datetimeStr);
  if (!parts) return null;
  if (parts.hours === 0 && parts.minutes === 0) return null;
  return `${String(parts.hours).padStart(2, '0')}:${String(parts.minutes).padStart(2, '0')}`;
};

export const toLocalDate = (datetimeStr) => {
  const parts = parseDateTimeParts(datetimeStr);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day, parts.hours, parts.minutes, parts.seconds);
};

export const formatDurationLabel = (startDatetime, endDatetime) => {
  const start = toLocalDate(startDatetime);
  const end = toLocalDate(endDatetime);
  if (!start || !end || end <= start) return null;
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  if (minutes < 1) return null;
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  const mins = minutes % 60;
  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
};

export const formatTimeGap = (currentEnd, nextStart) => {
  const end = toLocalDate(currentEnd);
  const start = toLocalDate(nextStart);
  if (!end || !start || start <= end) return null;
  const minutes = Math.round((start.getTime() - end.getTime()) / 60000);
  if (minutes < 15) return null;
  return formatDurationLabel(currentEnd, nextStart);
};

export const getCategoryEyebrow = (item, t) => {
  if (!item) return t('activityModal.other');
  if (item._isRoadTripCard) return t('itinerary.drive', { defaultValue: 'Drive' });
  if (isFlightItineraryItem(item)) return t('itinerary.flight');
  if (isCarRentalItineraryItem(item)) return t('itinerary.carRental');
  const type = item.transportation_type;
  if (item.category === 'transport' && type) {
    const key = `itinerary.transportTypes.${type}`;
    const translated = t(key);
    if (translated !== key) return translated;
    return type.replace(/_/g, ' ');
  }
  if (item.category) {
    const modalKey = `activityModal.${item.category}`;
    const fromModal = t(modalKey);
    if (fromModal !== modalKey) return fromModal;
    const itineraryKey = `itinerary.${item.category}`;
    const fromItinerary = t(itineraryKey);
    if (fromItinerary !== itineraryKey) return fromItinerary;
  }
  return item.category || t('activityModal.other');
};

export const parseTransportData = (item) => {
  let data = item?.transportation_data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return {};
    }
  }
  return data && typeof data === 'object' ? data : {};
};

export const getConfirmationRef = (item) => {
  const data = parseTransportData(item);
  return (
    data.confirmation_number ||
    data.booking_reference ||
    data.confirmation ||
    data.pnr ||
    null
  );
};

export const isCruiseItineraryItem = (item) => {
  if (!item) return false;
  const type = String(item.transportation_type || '').toLowerCase();
  if (type === 'cruise') return true;
  if (item.category !== 'transport') return false;
  const data = parseTransportData(item);
  return Boolean(data.cruise_line || data.ship_name || data.ports_of_call);
};

/**
 * True when the item occupies more than one calendar day (local wall clock),
 * using the same overnight cutoff as the day-by-day spine.
 */
export const itemSpansMultipleCalendarDays = (item) => {
  if (!item) return false;
  // Lazy require avoids a circular import with itineraryDaySpan → toLocalDate.
  // eslint-disable-next-line global-require
  const { getItineraryItemDayRange } = require('./itineraryDaySpan');
  const range = getItineraryItemDayRange(item.start_datetime, item.end_datetime);
  return Boolean(range && range.daySpan > 1);
};

/**
 * Hotels and multi-day transport bookings (flights, cruises, rentals, trains, …)
 * live in the reservations strip. Multi-day events/activities (e.g. conferences)
 * stay on the day-by-day spine with a Day X of Y indicator.
 */
export const isReservationOnlyItem = (item) => {
  if (!item) return false;
  if (item.category === 'hotel') return true;
  if (isCarRentalItineraryItem(item)) return true;
  if (isCruiseItineraryItem(item)) return true;
  if (item.category === 'transport' && itemSpansMultipleCalendarDays(item)) return true;
  return false;
};

const startOfLocalDay = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const itemOverlapsDay = (item, dayDate) => {
  const start = toLocalDate(item?.start_datetime);
  if (!start) return false;
  const end = toLocalDate(item?.end_datetime) || start;
  const current = startOfLocalDay(dayDate);
  const startDay = startOfLocalDay(start);
  const endDay = startOfLocalDay(end);
  if (!current || !startDay || !endDay) return false;
  return current >= startDay && current <= endDay;
};

export const isTransitItineraryItem = (item) => {
  if (!item) return false;
  if (item._isRoadTripCard) return true;
  if (isFlightItineraryItem(item)) return true;
  const roadTrip = parseTransportData(item).road_trip_data;
  return Boolean(roadTrip && (roadTrip.orderedStops || roadTrip.encodedPolyline || roadTrip.groupId));
};

export const dayHasTransit = (dayDate, items = []) =>
  items.some((item) => isTransitItineraryItem(item) && itemOverlapsDay(item, dayDate));

export const getEmptyDayStatusKey = (dayDate, items = []) =>
  dayHasTransit(dayDate, items) ? 'itinerary.status.inTransit' : 'itinerary.status.openNothingPlanned';

export const getReservationStatus = (item, kind, t) => {
  const start = toLocalDate(item?.start_datetime);
  const end = toLocalDate(item?.end_datetime) || start;
  if (!start) return null;
  const now = new Date();
  if (kind === 'hotel') {
    if (end && end < now) return t('itinerary.status.checkedOut');
    if (start <= now && end && end >= now) return t('itinerary.status.current');
    return t('itinerary.status.confirmed');
  }
  if (kind === 'rental') {
    if (end && end < now) return t('itinerary.status.returned');
    if (start <= now && end && end >= now) return t('itinerary.status.pickedUp');
    return t('itinerary.status.confirmed');
  }
  if (kind === 'cruise') {
    if (end && end < now) return t('itinerary.status.completed');
    if (start <= now && end && end >= now) return t('itinerary.status.onboard', { defaultValue: 'On board' });
    return t('itinerary.status.confirmed');
  }
  if (kind === 'transport') {
    if (end && end < now) return t('itinerary.status.completed');
    if (start <= now && end && end >= now) return t('itinerary.status.inTransit');
    return t('itinerary.status.confirmed');
  }
  if (end && end < now) return t('itinerary.status.completed');
  if (start <= now && end && end >= now) return t('itinerary.status.inFlight');
  return t('itinerary.status.scheduled');
};

export const getDayTimeSpan = (items, formatTime) => {
  if (!items?.length) return null;
  const first = items[0];
  const last = items[items.length - 1];
  const start = formatTime(first.start_datetime);
  const end = last.end_datetime ? formatTime(last.end_datetime) : formatTime(last.start_datetime);
  if (!start) return null;
  if (!end || end === start) return start;
  return `${start} – ${end}`;
};
